import { useEffect, useState, useCallback } from "react";
import { adminApi, formatApiError } from "../../contexts/AuthContext";
import {
  FileText,
  Save,
  RotateCcw,
  Eye,
  Code,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Lock,
  Tag,
  Info,
  Plus,
  Trash2,
  X,
} from "lucide-react";

const categoryBadge = {
  system: "bg-blue-50 text-blue-700 border-blue-200",
  broadcast: "bg-purple-50 text-purple-700 border-purple-200",
  custom: "bg-gray-50 text-gray-700 border-gray-200",
};

export default function EmailTemplates() {
  const [templates, setTemplates] = useState([]);
  const [variables, setVariables] = useState({});
  const [activeKey, setActiveKey] = useState(null);
  const [detail, setDetail] = useState(null);
  const [draft, setDraft] = useState({ subject: "", html_body: "", text_body: "" });
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [viewMode, setViewMode] = useState("edit"); // edit | preview
  const [msg, setMsg] = useState(null); // { type: "success" | "error", text }

  // Create/delete
  const [createOpen, setCreateOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/email-templates");
      setTemplates(data.templates || []);
      setVariables(data.variables || {});
      if ((data.templates || []).length && !activeKey) {
        setActiveKey(data.templates[0].key);
      }
    } catch (e) {
      setMsg({ type: "error", text: formatApiError(e) });
    } finally {
      setLoading(false);
    }
  }, [activeKey]);

  const loadDetail = useCallback(async (key) => {
    if (!key) return;
    setDetailLoading(true);
    try {
      const { data } = await adminApi.get(`/admin/email-templates/${key}`);
      setDetail(data);
      setDraft({
        subject: data.subject || "",
        html_body: data.html_body || "",
        text_body: data.text_body || "",
      });
      setPreview(null);
      setViewMode("edit");
    } catch (e) {
      setMsg({ type: "error", text: formatApiError(e) });
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  useEffect(() => {
    if (activeKey) loadDetail(activeKey);
  }, [activeKey, loadDetail]);

  const refreshPreview = async () => {
    if (!activeKey) return;
    setPreviewLoading(true);
    try {
      const { data } = await adminApi.post(`/admin/email-templates/${activeKey}/preview`, {
        subject: draft.subject,
        html_body: draft.html_body,
        text_body: draft.text_body,
      });
      setPreview(data);
    } catch (e) {
      setMsg({ type: "error", text: formatApiError(e) });
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleTogglePreview = async () => {
    if (viewMode === "edit") {
      await refreshPreview();
      setViewMode("preview");
    } else {
      setViewMode("edit");
    }
  };

  const handleSave = async () => {
    if (!activeKey) return;
    setSaving(true);
    setMsg(null);
    try {
      await adminApi.put(`/admin/email-templates/${activeKey}`, draft);
      setMsg({ type: "success", text: "Template saved successfully" });
      setTimeout(() => setMsg(null), 3000);
      fetchTemplates();
    } catch (e) {
      setMsg({ type: "error", text: formatApiError(e) });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!activeKey) return;
    if (!window.confirm(`Reset "${detail?.name || activeKey}" to the default template? Your changes will be lost.`))
      return;
    setResetting(true);
    setMsg(null);
    try {
      await adminApi.post(`/admin/email-templates/${activeKey}/reset`);
      setMsg({ type: "success", text: "Template reset to default" });
      setTimeout(() => setMsg(null), 3000);
      loadDetail(activeKey);
      fetchTemplates();
    } catch (e) {
      setMsg({ type: "error", text: formatApiError(e) });
    } finally {
      setResetting(false);
    }
  };

  const handleDelete = async () => {
    if (!activeKey || !detail) return;
    if (detail.is_system) return;
    if (!window.confirm(`Delete template "${detail.name}"? This cannot be undone.`)) return;
    setDeleting(true);
    setMsg(null);
    try {
      await adminApi.delete(`/admin/email-templates/${activeKey}`);
      setMsg({ type: "success", text: "Template deleted" });
      setTimeout(() => setMsg(null), 3000);
      setActiveKey(null);
      setDetail(null);
      fetchTemplates();
    } catch (e) {
      setMsg({ type: "error", text: formatApiError(e) });
    } finally {
      setDeleting(false);
    }
  };

  const handleCreated = (key) => {
    setCreateOpen(false);
    setMsg({ type: "success", text: "Template created" });
    setTimeout(() => setMsg(null), 3000);
    fetchTemplates();
    setActiveKey(key);
  };

  const isDirty =
    detail &&
    (draft.subject !== (detail.subject || "") ||
      draft.html_body !== (detail.html_body || "") ||
      draft.text_body !== (detail.text_body || ""));

  return (
    <div className="space-y-5" data-testid="email-templates-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: "Eczar, serif" }}>
          Email Templates
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Customize every email sent by the portal. Changes take effect immediately for new emails.
        </p>
      </div>

      {msg && (
        <div
          className={`px-4 py-2.5 rounded-md border text-sm flex items-start gap-2 ${
            msg.type === "success"
              ? "bg-green-50 border-green-200 text-green-700"
              : "bg-red-50 border-red-200 text-red-700"
          }`}
          data-testid="template-message"
        >
          {msg.type === "success" ? (
            <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
          ) : (
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          )}
          <span>{msg.text}</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-5">
        {/* Template list */}
        <aside className="lg:col-span-1 bg-white border border-gray-200 rounded-lg overflow-hidden h-fit sticky top-16">
          <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 flex items-center justify-between gap-2">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-600 flex items-center gap-2">
              <FileText className="w-3.5 h-3.5" />
              Templates ({templates.length})
            </h2>
            <button
              onClick={() => setCreateOpen(true)}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-[#FF9933] hover:bg-[#E67300] text-white rounded font-semibold transition-colors"
              data-testid="new-template-btn"
              title="Create a new template"
            >
              <Plus className="w-3 h-3" />
              New
            </button>
          </div>
          {loading ? (
            <div className="p-6 text-center">
              <Loader2 className="w-5 h-5 text-[#FF9933] animate-spin mx-auto" />
            </div>
          ) : (
            <ul data-testid="template-list">
              {templates.map((t) => (
                <li key={t.key}>
                  <button
                    onClick={() => setActiveKey(t.key)}
                    className={`w-full text-left px-4 py-3 border-b border-gray-100 last:border-0 transition-colors ${
                      activeKey === t.key
                        ? "bg-orange-50 border-l-4 border-l-[#FF9933]"
                        : "hover:bg-gray-50 border-l-4 border-l-transparent"
                    }`}
                    data-testid={`template-item-${t.key}`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <span className="text-sm font-medium text-gray-900 leading-tight">{t.name}</span>
                      {t.is_system && <Lock className="w-3 h-3 text-gray-400 flex-shrink-0 mt-0.5" title="System template" />}
                    </div>
                    <p className="text-xs text-gray-500 line-clamp-2 leading-snug">{t.description}</p>
                    <span
                      className={`inline-block mt-1.5 px-1.5 py-0.5 rounded text-[10px] font-medium border ${
                        categoryBadge[t.category] || categoryBadge.custom
                      }`}
                    >
                      {t.category}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>

        {/* Editor */}
        <section className="lg:col-span-3 space-y-4">
          {detailLoading || !detail ? (
            <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
              <Loader2 className="w-6 h-6 text-[#FF9933] animate-spin mx-auto" />
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="bg-white border border-gray-200 rounded-lg p-5">
                <div className="flex items-start justify-between flex-wrap gap-3">
                  <div className="flex-1 min-w-[200px]">
                    <div className="flex items-center gap-2 mb-1">
                      <h2 className="text-lg font-semibold text-gray-900">{detail.name}</h2>
                      {detail.is_system && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-blue-50 text-blue-700 border border-blue-200">
                          <Lock className="w-3 h-3" />
                          System
                        </span>
                      )}
                      <span
                        className={`px-2 py-0.5 rounded text-xs font-medium border ${
                          categoryBadge[detail.category] || categoryBadge.custom
                        }`}
                      >
                        {detail.category}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500">{detail.description}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleTogglePreview}
                      disabled={previewLoading}
                      className="inline-flex items-center gap-1.5 px-3 py-2 text-sm bg-white border border-gray-300 hover:border-[#FF9933] hover:text-[#FF9933] text-gray-700 rounded-md transition-colors disabled:opacity-60"
                      data-testid="toggle-preview-btn"
                    >
                      {previewLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : viewMode === "edit" ? (
                        <Eye className="w-4 h-4" />
                      ) : (
                        <Code className="w-4 h-4" />
                      )}
                      {viewMode === "edit" ? "Preview" : "Edit"}
                    </button>
                    {!detail.is_system && (
                      <button
                        onClick={handleDelete}
                        disabled={deleting}
                        className="inline-flex items-center gap-1.5 px-3 py-2 text-sm bg-white border border-gray-300 hover:border-red-500 hover:text-red-600 text-gray-700 rounded-md transition-colors disabled:opacity-60"
                        data-testid="delete-template-btn"
                      >
                        {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                        Delete
                      </button>
                    )}
                    <button
                      onClick={handleReset}
                      disabled={resetting}
                      className="inline-flex items-center gap-1.5 px-3 py-2 text-sm bg-white border border-gray-300 hover:border-amber-500 hover:text-amber-600 text-gray-700 rounded-md transition-colors disabled:opacity-60"
                      data-testid="reset-template-btn"
                    >
                      {resetting ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
                      Reset
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={saving || !isDirty}
                      className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm rounded-md font-semibold transition-colors ${
                        isDirty && !saving
                          ? "bg-[#FF9933] hover:bg-[#E67300] text-white shadow-sm"
                          : "bg-gray-200 text-gray-400 cursor-not-allowed"
                      }`}
                      data-testid="save-template-btn"
                    >
                      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      {saving ? "Saving..." : "Save"}
                    </button>
                  </div>
                </div>
              </div>

              {viewMode === "edit" ? (
                <>
                  {/* Variables helper */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Tag className="w-4 h-4 text-blue-700" />
                      <h3 className="text-xs font-semibold uppercase tracking-wider text-blue-900">
                        Available Variables
                      </h3>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(variables).map(([v, desc]) => (
                        <button
                          key={v}
                          type="button"
                          onClick={() => {
                            const token = `{{${v}}}`;
                            navigator.clipboard?.writeText(token);
                          }}
                          className="group inline-flex items-center gap-1 px-2 py-1 bg-white border border-blue-200 rounded text-xs font-mono text-blue-900 hover:bg-blue-100 transition-colors"
                          title={`${desc} — click to copy`}
                          data-testid={`variable-${v}`}
                        >
                          <code>{`{{${v}}}`}</code>
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-blue-700 mt-2 flex items-center gap-1">
                      <Info className="w-3 h-3" />
                      Click any variable to copy. Paste it anywhere in Subject, HTML or Text body.
                    </p>
                  </div>

                  {/* Subject */}
                  <div className="bg-white border border-gray-200 rounded-lg p-5">
                    <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-2">
                      Subject Line
                    </label>
                    <input
                      type="text"
                      value={draft.subject}
                      onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
                      className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-md focus:border-[#FF9933] focus:ring-2 focus:ring-[#FF9933]/20 outline-none"
                      data-testid="subject-input"
                    />
                  </div>

                  {/* HTML body */}
                  <div className="bg-white border border-gray-200 rounded-lg p-5">
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-xs font-semibold uppercase tracking-wider text-gray-600">
                        HTML Body
                      </label>
                      <span className="text-xs text-gray-400">{draft.html_body.length.toLocaleString()} chars</span>
                    </div>
                    <textarea
                      rows={16}
                      value={draft.html_body}
                      onChange={(e) => setDraft({ ...draft, html_body: e.target.value })}
                      className="w-full px-3 py-2.5 text-xs border border-gray-300 rounded-md focus:border-[#FF9933] focus:ring-2 focus:ring-[#FF9933]/20 outline-none font-mono resize-y"
                      spellCheck={false}
                      data-testid="html-body-input"
                    />
                  </div>

                  {/* Text body */}
                  <div className="bg-white border border-gray-200 rounded-lg p-5">
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-xs font-semibold uppercase tracking-wider text-gray-600">
                        Plain-Text Body
                      </label>
                      <span className="text-xs text-gray-400">Fallback for clients that don't support HTML</span>
                    </div>
                    <textarea
                      rows={8}
                      value={draft.text_body}
                      onChange={(e) => setDraft({ ...draft, text_body: e.target.value })}
                      className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-md focus:border-[#FF9933] focus:ring-2 focus:ring-[#FF9933]/20 outline-none font-mono resize-y"
                      spellCheck={false}
                      data-testid="text-body-input"
                    />
                  </div>
                </>
              ) : (
                /* Preview mode */
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="preview-area">
                  <div className="px-5 py-3 bg-gray-50 border-b border-gray-200">
                    <p className="text-xs text-gray-500 mb-1">Subject (as sent)</p>
                    <p className="text-sm font-medium text-gray-900">{preview?.subject || "(empty)"}</p>
                  </div>
                  <div className="p-5 bg-gray-100" style={{ minHeight: 400 }}>
                    {preview?.html ? (
                      <iframe
                        title="Email preview"
                        srcDoc={preview.html}
                        sandbox=""
                        className="w-full bg-white rounded-md shadow-sm"
                        style={{ minHeight: 600, border: "1px solid #e5e7eb" }}
                        data-testid="preview-iframe"
                      />
                    ) : (
                      <p className="text-center text-gray-400 text-sm py-12">
                        (HTML body is empty — add content in Edit mode)
                      </p>
                    )}
                  </div>
                  {preview?.text && (
                    <div className="border-t border-gray-200">
                      <div className="px-5 py-3 bg-gray-50 border-b border-gray-100">
                        <p className="text-xs font-semibold uppercase tracking-wider text-gray-600">
                          Plain-Text Fallback
                        </p>
                      </div>
                      <pre className="px-5 py-4 text-xs text-gray-700 font-mono whitespace-pre-wrap bg-white">
                        {preview.text}
                      </pre>
                    </div>
                  )}
                  <div className="px-5 py-3 bg-amber-50 border-t border-amber-200 text-xs text-amber-800">
                    <strong>Sample data used:</strong> {Object.entries(preview?.sample_variables || {}).map(([k, v]) => `${k}=${v}`).join(", ")}
                  </div>
                </div>
              )}
            </>
          )}
        </section>
      </div>

      {/* Create Template Modal */}
      {createOpen && (
        <CreateTemplateModal
          onClose={() => setCreateOpen(false)}
          onCreated={handleCreated}
          existingTemplates={templates}
        />
      )}
    </div>
  );
}

function CreateTemplateModal({ onClose, onCreated, existingTemplates }) {
  const [name, setName] = useState("");
  const [key, setKey] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("broadcast");
  const [duplicateFrom, setDuplicateFrom] = useState("");
  const [keyEdited, setKeyEdited] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");

  // Auto-derive key from name until user edits key manually
  useEffect(() => {
    if (!keyEdited) {
      const derived = name
        .toLowerCase()
        .replace(/[^a-z0-9_\s]/g, "")
        .trim()
        .replace(/\s+/g, "_")
        .slice(0, 60);
      setKey(derived);
    }
  }, [name, keyEdited]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setCreating(true);
    try {
      const { data } = await adminApi.post("/admin/email-templates", {
        key,
        name,
        description,
        category,
        duplicate_from: duplicateFrom || null,
      });
      onCreated(data.key);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setCreating(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="create-template-modal"
    >
      <div
        className="bg-white rounded-lg max-w-lg w-full shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">New Email Template</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded" disabled={creating}>
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
              {error}
            </div>
          )}
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1.5">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Shahi Snan Reminder"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:border-[#FF9933] focus:ring-2 focus:ring-[#FF9933]/20 outline-none"
              data-testid="new-template-name-input"
              autoFocus
            />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1.5">
              Key (Slug) <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              required
              value={key}
              onChange={(e) => {
                setKey(e.target.value.toLowerCase());
                setKeyEdited(true);
              }}
              placeholder="shahi_snan_reminder"
              pattern="[a-z0-9_]{3,60}"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:border-[#FF9933] focus:ring-2 focus:ring-[#FF9933]/20 outline-none font-mono"
              data-testid="new-template-key-input"
            />
            <p className="text-xs text-gray-500 mt-1">
              3-60 chars · lowercase letters, digits, underscores only · auto-generated from name
            </p>
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1.5">
              Description
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What is this template for?"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:border-[#FF9933] focus:ring-2 focus:ring-[#FF9933]/20 outline-none"
              data-testid="new-template-desc-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1.5">
                Category
              </label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:border-[#FF9933] focus:ring-2 focus:ring-[#FF9933]/20 outline-none bg-white"
                data-testid="new-template-category"
              >
                <option value="broadcast">Broadcast (usable in Broadcasts)</option>
                <option value="custom">Custom (hidden from Broadcasts picker)</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1.5">
                Start from
              </label>
              <select
                value={duplicateFrom}
                onChange={(e) => setDuplicateFrom(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:border-[#FF9933] focus:ring-2 focus:ring-[#FF9933]/20 outline-none bg-white"
                data-testid="new-template-duplicate"
              >
                <option value="">Blank</option>
                {existingTemplates.map((t) => (
                  <option key={t.key} value={t.key}>
                    Duplicate: {t.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              disabled={creating}
              className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md font-medium disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={creating || !name || !key}
              className="inline-flex items-center gap-2 px-5 py-2 text-sm bg-[#FF9933] hover:bg-[#E67300] text-white rounded-md font-semibold disabled:opacity-60 shadow-sm"
              data-testid="create-template-submit"
            >
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              {creating ? "Creating..." : "Create Template"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
